import React, { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// IMPORT THE LIBRARY, HOLDING THE NAMES OF ALL COUNTRIES IN THE WORLD
import { COUNTRY_NAMES } from "@/my_data/countryNames";

// FETCH THE NAMES OF ALL COUNTRIES IN THE WORLD -- library data, deliberately not translated
// (see RegistrationForm audit notes: country names come from world-countries, not app copy).
const countryList = COUNTRY_NAMES;

import { ROUTE } from "@/route/app_routes.js";
import { registerUser } from "@/services/authService";
import { getErrorMessage } from "@/services/apiClient";

const RegistrationForm = () => {

  const router = useRouter();
  const { t } = useTranslation();
  const [focusedInput, setFocusedInput] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState(null);

  const ages = Array.from({ length: 52 }, (_, i) => 18 + i); // [18...69]

  // Built inside the component (not at module scope) so validation messages respond to the
  // active language -- zod schemas can't call the useTranslation() hook themselves.
  const registerSchema = useMemo(
    () =>
      z.object({
        fname: z.string().min(1, t("auth.validation.firstNameRequired")).max(255),
        lname: z.string().min(1, t("auth.validation.surnameRequired")).max(255),
        email: z.string().min(1, t("auth.validation.emailRequiredGeneric")).email(t("auth.validation.emailInvalid")),
        country: z.string().min(1, t("auth.validation.countryRequired")),
        age: z.coerce.number({ invalid_type_error: t("auth.validation.ageRequired") }).min(2, t("auth.validation.ageRequired")),
        gender: z.enum(["male", "female", "other"], { errorMap: () => ({ message: t("auth.validation.genderRequired") }) }),
        password: z.string().min(8, t("auth.validation.passwordMinLength")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (values) => {
    setServerError(null);
    try {
      await registerUser(values);
      toast.success(t("auth.register.accountCreatedToast"));
      router.push(`${ROUTE.verifyEmail}?email=${encodeURIComponent(values.email)}`);
    } catch (error) {
      const message = getErrorMessage(error);
      setServerError(message);
      toast.error(message);
    }
  };

  return (

    <>
      <div className="rbt-auth-split__form-card rbt-auth-split__form-card--wide">

        <div className="rbt-auth-split__welcome">
          <h1 className="title">{t("auth.register.joinAcademy")}</h1>
          <p>{t("auth.register.subheading")}</p>
        </div>

        {serverError && (
          <div className="rbt-auth-split__alert" role="alert">
            <i className="feather-alert-circle"></i>
            <span>{serverError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>

          <div className="rbt-auth-split__field-row">
            <div className="rbt-auth-split__field">
              <div className={`form-group ${focusedInput === "firstname" ? "focused" : ""}`}>
                <i className="feather-user rbt-auth-split__field-icon"></i>
                <input
                  id="firstname"
                  type="text"
                  placeholder=" "
                  autoComplete="given-name"
                  {...register("fname")}
                  onFocus={() => setFocusedInput("firstname")}
                  onBlur={(e) => { setFocusedInput(null); register("fname").onBlur(e); }}
                />
                <label htmlFor="firstname">{t("auth.register.firstName")}</label>
                <span className="focus-border"></span>
              </div>
              {errors.fname && <span className="text-danger d-block mt--5">{errors.fname.message}</span>}
            </div>

            <div className="rbt-auth-split__field">
              <div className={`form-group ${focusedInput === "surname" ? "focused" : ""}`}>
                <i className="feather-user rbt-auth-split__field-icon"></i>
                <input
                  id="surname"
                  type="text"
                  placeholder=" "
                  autoComplete="family-name"
                  {...register("lname")}
                  onFocus={() => setFocusedInput("surname")}
                  onBlur={(e) => { setFocusedInput(null); register("lname").onBlur(e); }}
                />
                <label htmlFor="surname">{t("auth.register.surname")}</label>
                <span className="focus-border"></span>
              </div>
              {errors.lname && <span className="text-danger d-block mt--5">{errors.lname.message}</span>}
            </div>
          </div>

          <div className="rbt-auth-split__field">
            <div className={`form-group ${focusedInput === "email" ? "focused" : ""}`}>
              <i className="feather-mail rbt-auth-split__field-icon"></i>
              <input
                id="email"
                type="email"
                placeholder=" "
                autoComplete="email"
                {...register("email")}
                onFocus={() => setFocusedInput("email")}
                onBlur={(e) => { setFocusedInput(null); register("email").onBlur(e); }}
              />
              <label htmlFor="email">{t("auth.emailAddress")}</label>
              <span className="focus-border"></span>
            </div>
            {errors.email && <span className="text-danger d-block mt--5">{errors.email.message}</span>}
          </div>

          <div className="rbt-auth-split__field">
            <div className={`form-group ${focusedInput === "country" ? "focused" : ""}`}>
              <i className="feather-globe rbt-auth-split__field-icon"></i>
              <select
                required
                defaultValue=""
                {...register("country")}
                onFocus={() => setFocusedInput("country")}
                onBlur={(e) => { setFocusedInput(null); register("country").onBlur(e); }}
              >
                <option value="" disabled>{t("settings.profile.country")}</option>
                {countryList.map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <i className="feather-chevron-down rbt-auth-split__field-chevron"></i>
            </div>
            {errors.country && <span className="text-danger d-block mt--5">{errors.country.message}</span>}
          </div>

          <div className="rbt-auth-split__field-row">
            <div className="rbt-auth-split__field">
              <div className={`form-group ${focusedInput === "age" ? "focused" : ""}`}>
                <i className="feather-calendar rbt-auth-split__field-icon"></i>
                <select
                  required
                  defaultValue=""
                  {...register("age")}
                  onFocus={() => setFocusedInput("age")}
                  onBlur={(e) => { setFocusedInput(null); register("age").onBlur(e); }}
                >
                  <option value="" disabled>{t("settings.profile.age")}</option>
                  {ages.map((age) => (
                    <option key={age} value={age}>{age}</option>
                  ))}
                  <option value="70">{t("settings.profile.age70Plus")}</option>
                </select>
                <i className="feather-chevron-down rbt-auth-split__field-chevron"></i>
              </div>
              {errors.age && <span className="text-danger d-block mt--5">{errors.age.message}</span>}
            </div>

            <div className="rbt-auth-split__field">
              <div className={`form-group ${focusedInput === "gender" ? "focused" : ""}`}>
                <i className="feather-users rbt-auth-split__field-icon"></i>
                <select
                  required
                  defaultValue=""
                  {...register("gender")}
                  onFocus={() => setFocusedInput("gender")}
                  onBlur={(e) => { setFocusedInput(null); register("gender").onBlur(e); }}
                >
                  <option value="" disabled>{t("settings.profile.gender")}</option>
                  <option value="male">{t("settings.profile.male")}</option>
                  <option value="female">{t("settings.profile.female")}</option>
                  <option value="other">{t("settings.profile.other")}</option>
                </select>
                <i className="feather-chevron-down rbt-auth-split__field-chevron"></i>
              </div>
              {errors.gender && <span className="text-danger d-block mt--5">{errors.gender.message}</span>}
            </div>
          </div>

          <div className="rbt-auth-split__field">
            <div className={`form-group rbt-password-group ${focusedInput === "password" ? "focused" : ""}`}>
              <i className="feather-lock rbt-auth-split__field-icon"></i>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder=" "
                autoComplete="new-password"
                {...register("password")}
                onFocus={() => setFocusedInput("password")}
                onBlur={(e) => { setFocusedInput(null); register("password").onBlur(e); }}
              />
              <label htmlFor="password">{t("auth.password")}</label>
              <button
                type="button"
                className="rbt-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                <i className={showPassword ? "feather-eye-off" : "feather-eye"}></i>
              </button>
              <span className="focus-border"></span>
            </div>
            {errors.password && <span className="text-danger d-block mt--5">{errors.password.message}</span>}
          </div>

          <div className="form-submit-group mt--10">
            <button type="submit" className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100" disabled={isSubmitting}>
              <span className="icon-reverse-wrapper">
                <span className="btn-text">
                  {isSubmitting && <span className="rbt-auth-split__spinner" aria-hidden="true" />}
                  {isSubmitting ? t("auth.register.creatingAccount") : t("auth.register.createAccount")}
                </span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
              </span>
            </button>
          </div>

          <p className="rbt-auth-split__legal">
            {t("auth.register.legalPrefix")}{" "}
            <Link href={ROUTE.terms}>{t("auth.login.terms")}</Link> {t("auth.login.and")} <Link href={ROUTE.privacy}>{t("auth.login.privacyPolicy")}</Link>.
          </p>

        </form>
      </div>
    </>

  );

};

export default RegistrationForm;
